import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Gift, ToggleLeft, ToggleRight, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AdminReferOffersPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // Fetch offers
  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/refer-offers/status`);
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  // Toggle offer status
  const toggleOfferStatus = async (offerId, currentStatus) => {
    if (currentStatus) {
      // If currently active, show confirmation before pausing
      setConfirmModal({ offerId, currentStatus });
    } else {
      // If currently paused, resume immediately
      await updateOfferStatus(offerId, true);
    }
  };

  // Update offer status
  const updateOfferStatus = async (offerId, newStatus) => {
    setUpdating(offerId);
    setConfirmModal(null);
    
    try {
      await axios.post(`${API}/refer-offers/${offerId}/status`, {
        is_active: newStatus,
        admin_id: 'admin' // In real app, get from auth context
      });
      
      toast.success(newStatus ? 'Offer activated!' : 'Offer paused');
      fetchOffers();
    } catch (error) {
      console.error('Error updating offer:', error);
      toast.error('Failed to update offer status');
    } finally {
      setUpdating(null);
    }
  };

  // Get offer display info
  const getOfferInfo = (offerId) => {
    const info = {
      friends_10: { 
        title: 'Refer 10 Friends', 
        description: 'Unlock 1% by referring 10 friends',
        color: 'green'
      },
      friends_30: { 
        title: 'Refer 30 Friends', 
        description: 'Unlock another 1% by referring 30 friends',
        color: 'amber'
      },
      restaurants_5: { 
        title: 'Refer 5 Restaurants', 
        description: 'Unlock 1% by referring 5 restaurants',
        color: 'blue'
      },
      delivery_partners_5: { 
        title: 'Refer 5 Delivery Partners', 
        description: 'Unlock 1% by referring 5 delivery partners',
        color: 'purple'
      },
      friend_100: { 
        title: 'Refer a Friend - Earn ₹100', 
        description: 'Refer a friend and earn ₹100 after their first order',
        color: 'teal'
      },
      partner_150: { 
        title: 'Refer Partner - Earn ₹150', 
        description: 'Refer a restaurant or a delivery partner and earn ₹150 on their successful registration',
        color: 'rose'
      }
    };
    return info[offerId] || { title: offerId, description: '', color: 'gray' };
  };

  const getColorClasses = (color, isActive) => {
    const colors = {
      green: isActive ? 'bg-green-50 border-green-200' : 'bg-gray-100 border-gray-200',
      amber: isActive ? 'bg-amber-50 border-amber-200' : 'bg-gray-100 border-gray-200',
      blue: isActive ? 'bg-blue-50 border-blue-200' : 'bg-gray-100 border-gray-200',
      purple: isActive ? 'bg-purple-50 border-purple-200' : 'bg-gray-100 border-gray-200',
      teal: isActive ? 'bg-teal-50 border-teal-200' : 'bg-gray-100 border-gray-200',
      rose: isActive ? 'bg-rose-50 border-rose-200' : 'bg-gray-100 border-gray-200',
      gray: 'bg-gray-100 border-gray-200'
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              <Gift className="w-6 h-6 text-green-600" />
              <h1 className="text-xl font-bold">Manage Refer & Earn Offers</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Info Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Control cash burn by pausing offers</p>
                <p className="text-amber-700">Paused offers will be visible to users but greyed out and non-clickable.</p>
              </div>
            </div>

            {/* Offers List */}
            {offers.map((offer) => {
              const info = getOfferInfo(offer.id);
              const colorClasses = getColorClasses(info.color, offer.is_active);
              
              return (
                <div
                  key={offer.id}
                  className={`rounded-xl border p-5 transition-all ${colorClasses}`}
                  data-testid={`offer-${offer.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-bold text-lg ${offer.is_active ? 'text-gray-800' : 'text-gray-500'}`}>
                          {info.title}
                        </h3>
                        {!offer.is_active && (
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                            Paused
                          </span>
                        )}
                      </div>
                      <p className={`text-sm mt-1 ${offer.is_active ? 'text-gray-600' : 'text-gray-400'}`}>
                        {info.description}
                      </p>
                      {offer.paused_at && !offer.is_active && (
                        <p className="text-xs text-gray-400 mt-2">
                          Paused: {new Date(offer.paused_at).toLocaleString('en-IN')}
                        </p>
                      )}
                    </div>
                    
                    {/* Toggle Button */}
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${offer.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                        {offer.is_active ? 'ON' : 'OFF'}
                      </span>
                      <button
                        onClick={() => toggleOfferStatus(offer.id, offer.is_active)}
                        disabled={updating === offer.id}
                        className="focus:outline-none"
                        data-testid={`toggle-${offer.id}`}
                      >
                        {updating === offer.id ? (
                          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                        ) : offer.is_active ? (
                          <ToggleRight className="w-12 h-12 text-green-600 hover:text-green-700" />
                        ) : (
                          <ToggleLeft className="w-12 h-12 text-gray-400 hover:text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Pause this offer?</h3>
            <p className="text-gray-600 text-sm mb-6">
              Users will not be able to apply for this offer while it's paused. The offer will appear greyed out.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setConfirmModal(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => updateOfferStatus(confirmModal.offerId, false)}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                Pause
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReferOffersPage;
